import { format, formatDistanceToNowStrict, differenceInMinutes, differenceInHours } from 'date-fns';

export function formatTime(date) {
  return format(new Date(date), 'HH:mm');
}

export function formatDate(date) {
  return format(new Date(date), 'dd MMM yyyy');
}

export function formatDateTime(date) {
  return format(new Date(date), 'dd MMM yyyy, HH:mm');
}

export function formatDistanceShort(date) {
  const now = new Date();
  const mins = differenceInMinutes(now, date);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = differenceInHours(now, date);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return formatDistanceToNowStrict(date, { addSuffix: false });
}

// Returns "just now" or "Xm ago" / "Xh Ym ago" — never "just now ago".
export function formatTimeSince(date) {
  const short = formatDistanceShort(date);
  return short === 'just now' ? short : `${short} ago`;
}

export function formatDuration(totalSeconds) {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (hrs > 0) return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(mins)}:${pad(secs)}`;
}

export function formatDurationMinutes(totalSeconds) {
  const mins = Math.floor(totalSeconds / 60);
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}

export function todayISO() {
  return format(new Date(), 'yyyy-MM-dd');
}

// Returns the inclusive UTC date range that brackets a local day.
// Example: for EEST (UTC+3) and localDateISO="2026-05-15":
//   00:00 EEST = 2026-05-14T21:00Z, 23:59 EEST = 2026-05-15T20:59Z
//   → { from: '2026-05-14', to: '2026-05-15' }
// The API stores GSI1SK as the UTC date of startTime, so we must widen the
// query to cover both UTC dates the local day spans.
export function localDateToUtcRange(localDateISO) {
  const [y, m, d] = localDateISO.split('-').map(Number);
  const start = new Date(y, m - 1, d, 0, 0, 0);
  const end = new Date(y, m - 1, d, 23, 59, 59, 999);
  return {
    from: start.toISOString().substring(0, 10),
    to: end.toISOString().substring(0, 10),
  };
}

// True if the event's timestamp falls within the given local day.
export function isEventInLocalDay(event, localDateISO) {
  const ts = event?.startTime || event?.createdAt;
  if (!ts) return false;
  return format(new Date(ts), 'yyyy-MM-dd') === localDateISO;
}

// Local date bucket of an event ('YYYY-MM-DD' in the user's timezone).
export function eventLocalDate(event) {
  const ts = event?.startTime || event?.createdAt;
  return ts ? format(new Date(ts), 'yyyy-MM-dd') : '';
}

// ---------------------------------------------------------------------------
// v1.3.0 additions (docs/ux-modernisation-spec.md §2). New UI uses these; the
// helpers above are unchanged for existing callers.
// ---------------------------------------------------------------------------

let clockFormatter = null;
function getClockFormatter() {
  if (!clockFormatter) {
    try {
      clockFormatter = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
    } catch {
      clockFormatter = null;
    }
  }
  return clockFormatter;
}

// Locale-aware wall-clock time: "13:45" (en-GB) / "1:45 PM" (en-US). Returns '' for
// null/invalid input. Use this for new UI; `formatTime` (fixed HH:mm) stays for
// existing callers.
export function formatClock(date) {
  if (date === null || date === undefined || date === '') return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const fmt = getClockFormatter();
  return fmt ? fmt.format(d) : format(d, 'HH:mm');
}

// Minutes → "42 m" / "1 h 05 m" / "2 h". Negative or invalid → "0 m".
export function formatDurationShort(mins) {
  const total = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} m`;
  if (m === 0) return `${h} h`;
  return `${h} h ${String(m).padStart(2, '0')} m`;
}

// "Today" / "Yesterday" / "Tomorrow" / "Wed 3 Sep" (adds the year when it differs).
export function dayLabel(date) {
  if (date === null || date === undefined || date === '') return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((dayStart - todayStart) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays === 1) return 'Tomorrow';
  return d.getFullYear() === now.getFullYear()
    ? format(d, 'EEE d MMM')
    : format(d, 'EEE d MMM yyyy');
}

const defaultTs = (e) => e?.startTime || e?.createdAt;

// Groups events into local-day buckets, newest day first. Order within a day is the
// caller's input order (sort before grouping). Each group:
//   { dateISO: 'YYYY-MM-DD', date: Date (local midnight), label: dayLabel(), events: [] }
// `tsFn(event)` returns the timestamp to bucket on (default startTime || createdAt).
export function groupByDay(events, tsFn = defaultTs) {
  const groups = new Map();
  for (const e of events || []) {
    const ts = tsFn(e);
    if (!ts) continue;
    const d = ts instanceof Date ? ts : new Date(ts);
    if (Number.isNaN(d.getTime())) continue;
    const key = format(d, 'yyyy-MM-dd');
    let g = groups.get(key);
    if (!g) {
      g = {
        dateISO: key,
        date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
        label: dayLabel(d),
        events: [],
      };
      groups.set(key, g);
    }
    g.events.push(e);
  }
  return Array.from(groups.values()).sort((a, b) => (a.dateISO < b.dateISO ? 1 : a.dateISO > b.dateISO ? -1 : 0));
}
